import { useClientConfig } from '@framework/auth/client-config/ClientConfigSlice.tsx';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs } from './BreadcrumbContext.tsx';

export interface IBreadcrumbDocumentMetadataProps {}

/**
 * Tiene allineati i breadcrumbs con il titolo del documento HTML,
 * tramite helmet.
 */
export function BreadcrumbDocumentMetadata(
  props: IBreadcrumbDocumentMetadataProps,
) {
  const defaultTitle = useRef(document.title).current;

  const { jsonConfig, isLoaded } = useClientConfig();

  const items = useBreadcrumbs();
  const { t } = useTranslation();

  const title = useMemo(
    () =>
      items && items.length > 0
        ? [...items]
            .reverse()
            .map(b => b.title as string)
            .join(' - ')
        : undefined,
    [items],
  );

  // Annuncio per l'accessibilità.
  // Non vogliamo che il titolo si aggiorni se è vuoto, altrimenti
  // NVDA/VoiceOver annunciano "Navigato a" senza titolo.
  const [announcement, setAnnouncement] = useState<string>('');
  useEffect(() => {
    if (!isEmpty(title)) {
      setAnnouncement(
        t('route-announcer.nav-to-page', 'Navigato a {{title}}', { title }),
      );
    }
  }, [title, t]);

  // se presente denomiazioneBreve, lo mostriamo prima del titolo pagina
  // se non è presente denomiazioneBreve, mostriamo la denominazione di default (dopo il titolo)
  const showShortTitle = !isEmpty(jsonConfig?.denomiazioneBreve);

  const appTitle = isLoaded ? 'Framework Procedimenti | ' : '';
  const separator = title ? ' - ' : '';

  return (
    <>
      <Helmet>
        {/* Titolo HTML */}
        <title>
          {showShortTitle ? jsonConfig?.denomiazioneBreve + separator : ''}
          {title ?? ''}
          {showShortTitle ? '' : separator + appTitle + defaultTitle}
        </title>
      </Helmet>
      {/* Annuncio per l'accessibilità */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{ position: 'absolute', left: '-9999px' }}
      >
        {announcement}
      </div>
    </>
  );
}
